import type { UserProfilePreferencesForm } from "@shared/schemas";

import { Button } from "@bcc/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@bcc/components/ui/card";
import { Input } from "@bcc/components/ui/input";
import { Label } from "@bcc/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@bcc/components/ui/select";
import { AlertCircle } from "lucide-react";
import { Controller, type Control, type FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  setPreferencesViewProfileInfoCardTestId,
  setPreferencesViewProfileInfoDescriptionTestId,
  setPreferencesViewProfileInfoTitleTestId,
  setPreferencesViewSaveChangesButtonTestId,
} from "../views/preferences-view.utils";
import { RequiredAsterisk } from "./RequiredAsterisk";

interface ProfileInfoSectionProps {
  role: string;
  isLoading: boolean;
  isSaving: boolean;
  control: Control<UserProfilePreferencesForm>;
  errors: FieldErrors<UserProfilePreferencesForm>;
  isEditable: boolean;
  languageOptions: { label: string; value: string }[];
  translateValidationError: (key?: string) => string;
}

export const ProfileInfoSection = ({
  role,
  isLoading,
  isSaving,
  control,
  errors,
  isEditable,
  languageOptions,
  translateValidationError,
}: ProfileInfoSectionProps) => {
  const { t } = useTranslation();

  return (
    <Card data-testid={setPreferencesViewProfileInfoCardTestId()}>
      <CardHeader>
        <CardTitle data-testid={setPreferencesViewProfileInfoTitleTestId()}>{t("views.preferences.profileInfo.title")}</CardTitle>
        <CardDescription data-testid={setPreferencesViewProfileInfoDescriptionTestId()}>
          {t("views.preferences.profileInfo.description")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-2">
            <Label htmlFor="first-name">
              {t("views.preferences.profileInfo.firstName")}
              <RequiredAsterisk />
            </Label>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => <Input {...field} id="first-name" aria-invalid={!!errors.firstName} disabled={!isEditable} />}
            />
            <p className="flex min-h-4 items-center gap-1 text-xs text-destructive">
              {errors.firstName && <AlertCircle className="size-3 shrink-0" />}
              {translateValidationError(errors.firstName?.message as string | undefined)}
            </p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="last-name">
              {t("views.preferences.profileInfo.lastName")}
              <RequiredAsterisk />
            </Label>
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => <Input {...field} id="last-name" aria-invalid={!!errors.lastName} disabled={!isEditable} />}
            />
            <p className="flex min-h-4 items-center gap-1 text-xs text-destructive">
              {errors.lastName && <AlertCircle className="size-3 shrink-0" />}
              {translateValidationError(errors.lastName?.message as string | undefined)}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-2">
            <Label htmlFor="email">
              {t("views.preferences.profileInfo.email")}
              <RequiredAsterisk />
            </Label>
            <Controller
              name="email"
              control={control}
              render={({ field }) => <Input {...field} id="email" type="email" aria-invalid={!!errors.email} disabled readOnly />}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="role">{t("views.preferences.profileInfo.role")}</Label>
            <Input id="role" value={role} disabled readOnly />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-2">
            <Label htmlFor="language-locale-key">{t("views.preferences.profileInfo.language")}</Label>
            <Controller
              name="languageLocaleKey"
              control={control}
              render={({ field }) => (
                <Select value={field.value ?? ""} disabled>
                  <SelectTrigger id="language-locale-key" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {languageOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>
        <Button data-testid={setPreferencesViewSaveChangesButtonTestId()} type="submit" disabled={isLoading || isSaving || !isEditable}>
          {isSaving ? t("views.preferences.profileInfo.saving") : t("views.preferences.profileInfo.save")}
        </Button>
      </CardContent>
    </Card>
  );
};
